import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { JiraIssue, Task } from "axiosApi/models";
import JiraIssuesList from "./JiraIssuesList";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import Api from "axiosApi/api";
import { useApi } from '../../../api/ApiProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from "common/errorHandler";
import ErrorSummary from "components/common/ErrorSummary";

export type JiraIssueSearchProps = {
  idProject: number;
  handleIssuesAdd: Function;
  tasks: Task[];
};

const JiraIssueSearch = ({ idProject, handleIssuesAdd, tasks }: JiraIssueSearchProps) => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();

  const [errors, setErrors] = useState<{ term?: string }>({});
  const [term, setTerm] = useState('');
  const [jiraIssues, setJiraIssues] = useState<JiraIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNum, setPageNum] = useState(0);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const pageSize = 8;
  const WAIT_INTERVAL = 2000;
  const [timerID, setTimerID] = useState<NodeJS.Timeout | null>(null);
  const [lastSearchTerm, setLastSearchTerm] = useState('');

  const validate = (inputVal) => {
    let errors = {};
    if (!inputVal.term) {
      errors['term'] = intl.get('validate.errors.isRequired');
    }
    setErrors({ ...errors });
    return Object.keys(errors).length > 0 ? errors : null;
  };

  const doSearch = (_value?: string, _pageNum?: number) => {
    const searchTerm = _value ?? term;
    const searchPageNum = _pageNum ?? pageNum;

    if (validate({ term: searchTerm }) === null) {
      if (timerID) clearTimeout(timerID);
      const newTimerID = setTimeout(() => {
        setLoading(true);
        setLastSearchTerm(searchTerm);
        api.jobsApi.apiVversionTaskJiraSearchGet("1", idProject, searchTerm, searchPageNum, pageSize)
          .then((response) => {
            if (response.data.data) {
              const filteredIssues = response.data.data.queryResult.filter(issue => !isIssueAlreadyATask(issue.idJira));
              setJiraIssues([...filteredIssues]);
              const total = response.data.data.totalRecords - (filteredIssues.length > 0 ? (response.data.data.queryResult.length - filteredIssues.length) : 0);
              setTotalRecords(total);
            }
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
          })
          .finally(() => {
            setLoading(false);
          });
      }, WAIT_INTERVAL);
      setTimerID(newTimerID);
    }
  };

  const handleTermChange = (value: string) => {
    setTerm(value);
    setPageNum(0);
    if (timerID) clearTimeout(timerID);
    
    if (validate({ term: value }) === null) {
      const newTimerID = setTimeout(() => {
        setLoading(true);
        setLastSearchTerm(value);
        api.jobsApi.apiVversionTaskJiraSearchGet("1", idProject, value, 0, pageSize)
          .then((response) => {
            if (response.data.data) {
              const filteredIssues = response.data.data.queryResult.filter(issue => !isIssueAlreadyATask(issue.idJira));
              setJiraIssues([...filteredIssues]);
              const total = response.data.data.totalRecords - (filteredIssues.length > 0 ? (response.data.data.queryResult.length - filteredIssues.length) : 0);
              setTotalRecords(total);
            }
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
          })
          .finally(() => {
            setLoading(false);
          });
      }, WAIT_INTERVAL);
      setTimerID(newTimerID);
    }
  };

  const handleSearch = () => {
    setPageNum(0);
    if (timerID) clearTimeout(timerID);
    
    if (validate({ term }) === null) {
      setLoading(true);
      setLastSearchTerm(term);
      api.jobsApi.apiVversionTaskJiraSearchGet("1", idProject, term, 0, pageSize)
        .then((response) => {
          if (response.data.data) {
            const filteredIssues = response.data.data.queryResult.filter(issue => !isIssueAlreadyATask(issue.idJira));
            setJiraIssues([...filteredIssues]);
            const total = response.data.data.totalRecords - (filteredIssues.length > 0 ? (response.data.data.queryResult.length - filteredIssues.length) : 0);
            setTotalRecords(total);
          }
        })
        .catch((error) => {
          handleAPIError(error, toast, errors);
          setErrors({ ...errors });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleIssueSelect = (idJira: string, checked: boolean) => {
    setSelectedIssues(prev => checked ? [...prev, idJira] : prev.filter(id => id !== idJira));
  };

  const isIssueSelected = (idJira: string): boolean => {
    return selectedIssues.includes(idJira);
  };

  const handleIssueSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIssues(jiraIssues.map(issue => issue.idJira));
    } else {
      setSelectedIssues([]);
    }
  };

  const isIssueSelectedAll = (): boolean => {
    return selectedIssues.length === jiraIssues.length && jiraIssues.length > 0;
  };

  const isIssueAlreadyATask = (idJira: string): boolean => {
    return tasks.some(task => task.idJira === idJira);
  };

  const handleAddSelected = () => {
    const selected = jiraIssues.filter(j => selectedIssues.includes(j.idJira));
    handleIssuesAdd([...selected]);
    setSelectedIssues([]);
  };

  useEffect(() => {
    // Only search when pageNum changes and it's not the initial page (0)
    // Page 0 searches are handled by handleTermChange and handleSearch
    if (term.length > 0 && pageNum > 0 && term === lastSearchTerm) {
      doSearch();
    }
  }, [pageNum]);

  useEffect(() => {
    // Clean up timer on unmount
    return () => {
      if (timerID) clearTimeout(timerID);
    };
  }, [timerID]);

  useEffect(() => {
    if (jiraIssues.length > 0) {
      const filteredIssues = jiraIssues.filter(issue => !isIssueAlreadyATask(issue.idJira));
      const total = totalRecords - (jiraIssues.length - filteredIssues.length);
      
      // Only update if there's actually a difference
      if (filteredIssues.length !== jiraIssues.length) {
        setJiraIssues([...filteredIssues]);
        setTotalRecords(Math.max(0, total));
      }
    }
  }, [tasks]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <ErrorSummary errors={errors} showEverything={true} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label={intl.get('jiraIssueSearch.label')}
            placeholder={intl.get('jiraIssueSearch.placeholder')}
            id="term"
            name="term"
            value={term}
            onChange={(e) => handleTermChange(e.target.value)}
            error={!!errors.term}
            helperText={errors.term}
            autoFocus
            autoComplete="off"
            size="small"
            variant="outlined"
          />
        </Grid>

        <Grid
          display="flex"
          alignItems="flex-end"
          size={{
            xs: 12,
            md: 3
          }}>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            startIcon={<SearchIcon />}
          >
            {intl.get('jiraIssueSearch.search.button')}
          </Button>
        </Grid>
      </Grid>
      {term.length > 0 && (
        <Box mt={3}>
          <JiraIssuesList
            jiraIssues={jiraIssues}
            handleIssuesAdd={handleIssuesAdd}
            handleIssueSelect={handleIssueSelect}
            handleIssueSelectAll={handleIssueSelectAll}
            isIssueSelected={isIssueSelected}
            isIssueSelectedAll={isIssueSelectedAll}
            setPageNum={setPageNum}
            pageNum={pageNum}
            pageSize={pageSize}
            totalRecords={totalRecords}
            loading={loading}
          />
        </Box>
      )}
      {selectedIssues.length > 0 && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              handleAddSelected();
            }}
          >
            {intl.get('jiraIssueSearch.addSelected.button')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default JiraIssueSearch;